/**
 * Criteria picker — multi-select grouped by "Whole response" (artifact-only
 * system criteria), "Standard" (system) and "My criteria" (user). Opens a CRUD
 * drawer for managing user criteria.
 *
 * The groups shown depend on the run scope: criteria that only mean something
 * against the assembled response are hidden at section scope, because the
 * server rejects that combination (400 CRITERION_SCOPE_MISMATCH).
 */

import { useEffect, useMemo } from 'react'
import { Check, Plus, Settings2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useReviewStore } from '@/stores/review-store'
import { criterionAppliesToScope, type ReviewCriterion } from '@/lib/schemas'

interface CriteriaPickerProps {
  scope: 'section' | 'artifact'
  onManageClick?: () => void
}

export function CriteriaPicker({ scope, onManageClick }: CriteriaPickerProps) {
  const criteria = useReviewStore((s) => s.criteria)
  const selectedIds = useReviewStore((s) => s.selectedCriteriaIds)
  const loading = useReviewStore((s) => s.criteriaLoading)
  const loadCriteria = useReviewStore((s) => s.loadCriteria)
  const toggleCriterion = useReviewStore((s) => s.toggleCriterion)

  useEffect(() => {
    if (!criteria && !loading) {
      void loadCriteria()
    }
  }, [criteria, loading, loadCriteria])

  // Artifact-only criteria lead at document scope — they are the reason
  // someone switched — and disappear entirely at section scope.
  const wholeResponse = useMemo(
    () =>
      scope === 'artifact'
        ? (criteria?.system ?? []).filter((c) => c.appliesTo === 'artifact')
        : [],
    [criteria, scope],
  )
  const system = useMemo(
    () =>
      (criteria?.system ?? []).filter(
        (c) => c.appliesTo !== 'artifact' && criterionAppliesToScope(c, scope),
      ),
    [criteria, scope],
  )
  const mine = useMemo(
    () => (criteria?.mine ?? []).filter((c) => criterionAppliesToScope(c, scope)),
    [criteria, scope],
  )

  if (loading && !criteria) {
    return <div className="text-meta">Loading criteria…</div>
  }

  return (
    <div className="space-y-3">
      {wholeResponse.length > 0 && (
        <CriteriaGroup
          label="Whole response"
          caption="Reads the assembled response for repetition, contradictions and gaps."
          emptyMessage="No whole-response criteria available."
          items={wholeResponse}
          selectedIds={selectedIds}
          onToggle={toggleCriterion}
        />
      )}
      <CriteriaGroup
        label="Standard"
        emptyMessage="No system criteria available."
        items={system}
        selectedIds={selectedIds}
        onToggle={toggleCriterion}
      />
      <CriteriaGroup
        label="My criteria"
        emptyMessage="You haven't saved any custom criteria yet."
        items={mine}
        selectedIds={selectedIds}
        onToggle={toggleCriterion}
        action={
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={onManageClick}
            aria-label="Manage my criteria"
          >
            <Settings2 className="h-3 w-3" />
            Manage
          </Button>
        }
      />
    </div>
  )
}

interface CriteriaGroupProps {
  label: string
  caption?: string
  emptyMessage: string
  items: ReviewCriterion[]
  selectedIds: string[]
  onToggle: (id: string) => void
  action?: React.ReactNode
}

function CriteriaGroup({
  label,
  caption,
  emptyMessage,
  items,
  selectedIds,
  onToggle,
  action,
}: CriteriaGroupProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
          {label}
        </span>
        {action && <span className="ml-auto">{action}</span>}
      </div>
      {caption && <p className="text-meta">{caption}</p>}
      {items.length === 0 ? (
        <p className="text-meta italic">{emptyMessage}</p>
      ) : (
        <ul className="space-y-1">
          {items.map((criterion) => {
            const isSelected = selectedIds.includes(criterion.id)
            return (
              <li key={criterion.id}>
                <button
                  type="button"
                  onClick={() => onToggle(criterion.id)}
                  className={cn(
                    'w-full text-left rounded-md border px-2.5 py-1.5 shadow-sm transition-colors',
                    isSelected
                      ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft)/0.5)] hover:bg-[hsl(var(--brand-soft)/0.7)]'
                      : 'border-border bg-card hover:bg-muted/40',
                  )}
                  aria-pressed={isSelected}
                >
                  <div className="flex items-start gap-2">
                    <span
                      className={cn(
                        'mt-0.5 h-4 w-4 shrink-0 rounded border flex items-center justify-center',
                        isSelected
                          ? 'bg-[hsl(var(--brand))] border-[hsl(var(--brand))]'
                          : 'border-border',
                      )}
                      aria-hidden
                    >
                      {isSelected && <Check className="h-3 w-3 text-[hsl(var(--brand-foreground))]" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">{criterion.name}</div>
                      {criterion.description && (
                        <div className="text-[11px] text-muted-foreground line-clamp-2">
                          {criterion.description}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export function CriteriaPickerHint({ count }: { count: number }) {
  if (count === 0) {
    return (
      <p className="text-[11px] text-muted-foreground">
        Pick at least one criterion to run a review.
      </p>
    )
  }
  return (
    <p className="text-[11px] text-muted-foreground">
      {/* Irregular plural — suffixing 'a' onto "criterion" produced "criteriona". */}
      {count} {count === 1 ? 'criterion' : 'criteria'} selected
    </p>
  )
}

export function NewCriterionButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={onClick}>
      <Plus className="h-3 w-3" />
      New criterion
    </Button>
  )
}
